import { useState, useEffect, useRef } from 'react';
import './App.css';
import './cursor.js';

const menuItems = [
  { id: 'about', label: 'About' },
  { id: 'services', label: 'Services' },
  { id: 'electron-family-office', label: 'Family Office' },
  { id: 'contact', label: 'Contact' }
];

const locations = [
  { name: 'London', image: '/images/london.jpg' },
  { name: 'Europe', image: '/images/monaco.jpg' },
  { name: 'USA', image: '/images/usa.jpg' },
  { name: 'Dubai', image: '/images/dubai.jpg' }
];

function App() {
  const linkedinWebUrl = 'https://www.linkedin.com/in/ben-doweck-5506b012/';
  const linkedinAndroidIntentUrl = 'intent://www.linkedin.com/in/ben-doweck-5506b012/#Intent;package=com.linkedin.android;scheme=https;S.browser_fallback_url=https%3A%2F%2Fwww.linkedin.com%2Fin%2Fben-doweck-5506b012%2F;end';
  const primaryEmail = 'bd@peregrinewealth.co.uk';
  const electronEmail = 'bd@electron-holdings.com';
  const isMobileDevice = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showHeroPlayButton, setShowHeroPlayButton] = useState(false);
  const heroVideoRef = useRef(null);

  const handleEmailClick = (event, email) => {
    event.preventDefault();

    const mailtoUrl = `mailto:${email}`;
    const gmailComposeUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(email)}`;

    const fallbackTimeoutId = window.setTimeout(() => {
      window.open(gmailComposeUrl, '_blank', 'noopener,noreferrer');
    }, 900);

    const handleVisibilityChange = () => {
      if (document.hidden) {
        window.clearTimeout(fallbackTimeoutId);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.location.assign(mailtoUrl);
  };

  const handleLinkedInClick = (event) => {
    if (!isMobileDevice) {
      return;
    }

    event.preventDefault();

    const isAndroid = /Android/i.test(navigator.userAgent);
    if (isAndroid) {
      window.location.assign(linkedinAndroidIntentUrl);
      return;
    }

    window.location.assign(linkedinWebUrl);
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      const elements = document.querySelectorAll('.fade-in-section, .slide-in-left, .slide-in-right, .fade-delay');
      elements.forEach((element) => {
        element.classList.add('fade-in-visible', 'visible');
      });
      return;
    }

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('fade-in-visible');
          
          // Trigger child animations
          const slideElements = entry.target.querySelectorAll('.slide-in-left, .slide-in-right, .fade-delay');
          slideElements.forEach((el, index) => {
            setTimeout(() => {
              el.classList.add('visible');
            }, index * 100);
          });
        } else {
          // Reverse animation when scrolling away
          entry.target.classList.remove('fade-in-visible');
          
          // Remove child animation classes
          const slideElements = entry.target.querySelectorAll('.slide-in-left, .slide-in-right, .fade-delay');
          slideElements.forEach(el => {
            el.classList.remove('visible');
          });
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll('.fade-in-section');
    elements.forEach(el => observer.observe(el));

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const video = heroVideoRef.current;
    if (!video) {
      return;
    }

    let cancelled = false;

    const tryAutoplay = async () => {
      try {
        const playPromise = video.play();
        if (playPromise && typeof playPromise.then === 'function') {
          await playPromise;
        }
        if (!cancelled) {
          setShowHeroPlayButton(false);
        }
      } catch {
        if (!cancelled) {
          setShowHeroPlayButton(true);
        }
      }
    };

    const handlePlaying = () => {
      setShowHeroPlayButton(false);
    };

    video.addEventListener('playing', handlePlaying);
    tryAutoplay();

    return () => {
      cancelled = true;
      video.removeEventListener('playing', handlePlaying);
    };
  }, []);

  const handleHeroVideoPlay = async () => {
    const video = heroVideoRef.current;
    if (!video) {
      return;
    }

    try {
      await video.play();
      setShowHeroPlayButton(false);
    } catch {
      setShowHeroPlayButton(true);
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setMenuOpen(false);
  };

  return (
    <div className="App">
      {/* Navigation */}
      <nav className={`nav ${scrolled ? 'scrolled' : ''}`}>
        <img 
          src="/images/logo.png" 
          alt="Peregrine Wealth" 
          className={`nav-logo ${scrolled ? 'visible' : ''}`}
        />
        <button 
          className="burger-menu" 
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </nav>

      {/* Menu Overlay */}
      <div className={`menu-overlay ${menuOpen ? 'open' : ''}`}>
        <ul>
          {menuItems.map((item) => (
            <li key={item.id}>
              <button onClick={() => scrollToSection(item.id)}>{item.label}</button>
            </li>
          ))}
        </ul>
      </div>

      {/* Hero Section */}
      <section id="hero" className="hero fade-in-section">
        <video ref={heroVideoRef} autoPlay loop muted playsInline preload="auto" className="hero-video">
          <source src="/images/hero.mp4" type="video/mp4" />
        </video>
        <div className="hero-overlay"></div>
        {showHeroPlayButton && (
          <div className="hero-play-overlay">
            <button type="button" className="hero-play-button" onClick={handleHeroVideoPlay} aria-label="Play hero video">
              Tap to play video
            </button>
          </div>
        )}
        <div className="hero-content">
          <img src="/images/logo.png" alt="Peregrine Wealth" className={`hero-logo ${scrolled ? 'faded' : ''}`} />
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about glass-section fade-in-section">
        <div className="container">
          <h2 className="slide-in-left">About</h2>
          <p className="lead slide-in-right">
            We are a capital advisory firm specialising in structured finance and asset backed lending, focused on
            complex time sensitive transactions where conventional credit frameworks are not fit for purpose. We
            arrange bridging and term facilities for real estate, marine, aviation, securities and luxury assets
            across the UK, USA, Dubai and Central Europe.
          </p>
          <p className="lead slide-in-left">
            We also represent Electron Family Office, a private principal investment office deploying proprietary and
            partner capital combining the capital preservation discipline of a family office with the structured
            underwriting standards of an institutional private credit platform.
          </p>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services glass-section fade-in-section">
        <div className="hero-image-bg services-image-bg">
          <img src="/images/1.jpg" alt="Services" loading="lazy" decoding="async" />
        </div>
        <div className="hero-overlay"></div>
        <div className="container">
          <h2 className="slide-in-left">Services</h2>
          <div className="service-list">
            <article className="service-item slide-in-right">
              <h3>Real Estate</h3>
              <p>
                Peregrine Wealth arranges senior bridging facilities from £5m+ across the UK, USA, Dubai and Central
                Europe. We support prime and super-prime residential, commercial, industrial/logistics and selective
                land-backed transactions where liquidity and exit are clear. We structure around multi-jurisdictional
                ownership and can cross-collateralise across jurisdictions and asset classes where required.
              </p>
            </article>

            <article className="service-item slide-in-left">
              <h3>Luxury Assets</h3>
              <p>
                Peregrine Wealth arranges asset-backed facilities against fine art, collector cars, gemstones and
                other luxury assets, providing liquidity without a forced sale or disrupting wider strategy.
                Transactions are structured around robust security, verified ownership, independent valuation and an
                executable exit, with terms calibrated to asset type and liquidity. Where appropriate, we can
                cross-collateralise across multiple asset classes to deliver bespoke structures.
              </p>
            </article>

            <article className="service-item slide-in-right">
              <h3>Securities</h3>
              <p>
                We arrange facilities against listed securities from £2m+, enabling clients to unlock capital without
                selling positions. Facilities can be structured interest only, retained or PIK and we can accommodate
                mixed collateral packages where appropriate. Alongside private credit execution we also structure
                exchange listed bonds and listed notes/certificates to widen capital access and introduce tradability.
                Execution is rapid once valuation and custody are verified.
              </p>
            </article>

            <article className="service-item slide-in-left">
              <h3>Marine &amp; Aviation</h3>
              <p>
                Peregrine Wealth arranges short and long-term debt against yachts, commercial vessels, private
                aircraft and other eligible assets delivering asset-backed liquidity for acquisitions, refinancings and
                time-critical situations. Facilities are structured around asset quality, jurisdiction,
                charter/operating profile and enforceability, with terms calibrated to risk, liquidity and a credible
                exit. We can accommodate complex ownership and registration structures and where it improves leverage
                and execution, cross-collateralise across marine, aviation, real estate and other qualifying assets.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* Electron Family Office Section */}
      <section id="electron-family-office" className="electron-section hero-section-alt fade-in-section">
        <div className="hero-image-bg">
          <img src="/images/2.jpg" alt="Luxury House" loading="lazy" decoding="async" />
        </div>
        <div className="hero-overlay"></div>
        <div className="hero-text-content">
          <img
            src="/images/elec_gold.png"
            alt="Electron Family Office"
            className="electron-logo slide-in-left"
            loading="lazy"
            decoding="async"
          />
          <p className="slide-in-left">
            Peregrine Wealth works with Electron Family Office in relation to select secured, asset-backed lending
            opportunities. Electron Family Office deploys proprietary balance sheet capital across multiple
            jurisdictions, providing senior secured financing against defensible collateral with clearly defined exit
            pathways.
          </p>
          <p className="slide-in-right">
            Electron typically considers opportunities in the £10m–£200m+ range, generally 6–36 months in tenor,
            secured via first-ranking charges or equivalent control mechanisms,including share pledges, debentures,
            custody arrangements and controlled SPV structures.
          </p>
          <p className="slide-in-left">
            Collateral may include real estate, infrastructure and energy generation, aviation and transport, shipping,
            industrial and specialist assets, listed securities and alternative assets where ownership, valuation and
            security can be clearly established.
          </p>
          <p className="slide-in-right">
            All opportunities are subject to independent underwriting, due diligence and internal approval.
          </p>
          <div className="electron-cta slide-in-left">
            <a
              href={`mailto:${electronEmail}`}
              className="contact-button"
              onClick={(event) => handleEmailClick(event, electronEmail)}
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>

      {/* Locations Section */}
      <section className="locations fade-in-section">
        <div className="container">
          <div className="location-grid">
            {locations.map((city, index) => (
              <div key={index} className="location-card fade-delay">
                <img src={city.image} alt={city.name} loading="lazy" decoding="async" />
                <div className="location-overlay">
                  <h3>{city.name}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact glass-section fade-in-section">
        <div className="container">
          <h2 className="slide-in-left">Contact</h2>
          <p className="slide-in-right">For enquiries please contact us.</p>
          <a
            href={`mailto:${primaryEmail}`}
            className="contact-button slide-in-left"
            onClick={(event) => handleEmailClick(event, primaryEmail)}
          >
            Contact Us
          </a>
          <a
            href={linkedinWebUrl}
            className="linkedin-link slide-in-left"
            target={isMobileDevice ? undefined : '_blank'}
            rel={isMobileDevice ? undefined : 'noopener noreferrer'}
            aria-label="Visit LinkedIn profile"
            onClick={handleLinkedInClick}
          >
            <svg viewBox="0 0 24 24" role="img" aria-hidden="true" focusable="false">
              <path d="M6.94 8.5V20H3.12V8.5h3.82Zm.25-3.55c0 1.1-.83 1.98-2.16 1.98-1.28 0-2.12-.88-2.12-1.98 0-1.13.86-2 2.16-2 1.3 0 2.1.87 2.12 2Zm13.81 8.46V20h-3.8v-6.15c0-1.54-.55-2.58-1.94-2.58-1.05 0-1.68.71-1.96 1.4-.1.25-.13.6-.13.95V20H9.36s.05-10.34 0-11.5h3.8v1.63c.51-.78 1.41-1.9 3.42-1.9 2.5 0 4.42 1.63 4.42 5.13Z" />
            </svg>
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <p className="copyright">© {new Date().getFullYear()} Peregrine Wealth. All rights reserved.</p>
          <p className="footer-terms">
            The statements and data on this website have been compiled by Peregrine Wealth to the best of its
            knowledge for informational and marketing purposes only. The contents of this website constitute neither a
            solicitation nor an offer or recommendation. It also does not constitute advice on legal, tax, or other
            matters. Terms &amp; Conditions apply.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
